import React, { FC, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { Alert, Button, Col, FormText, Input, Label, Row } from 'reactstrap';
import { Policy } from '../../../../shared/policies';
import { TranslationKey, useTranslation } from '../../../translations';
import { Fetcher, useConvention, useFetcher, useInputState } from '../../../utils';
import { captureError } from '../../../utils/errorHandling';

export const TermsSettings: FC = () => {
  const { identityVerification } = useConvention();
  const fetcher = useFetcher(async () => {
    return await api.getPolicies();
  }, []);

  if (!fetcher.success) {
    return <Fetcher result={fetcher} />;
  }

  const { refresh, data } = fetcher;

  const policies = data;

  return (
    <div>
      <TermsElement
        description="terms.policy_explaining_how_the_organization"
        displayName="terms.privacy_terms"
        name="privacy"
        policies={policies}
        refresh={refresh}
      />
      <hr />
      <TermsElement
        description="terms.the_organization_conventions_terms"
        displayName="terms.convention_policy_terms_"
        name="terms"
        policies={policies}
        refresh={refresh}
      />
      <hr />
      <TermsElement
        description="terms.error_message_shown_to_users"
        displayName="terms.child_registration_error"
        name="childRegistrationError"
        policies={policies}
        refresh={refresh}
      />
      <hr />
      <TermsElement
        description="terms.warning_message_shown_to_users"
        displayName="terms.minor_registration_warning"
        name="underageWarning"
        policies={policies}
        refresh={refresh}
      />
      <hr />
      <TermsElement
        description="terms.terms_shown_to_parents_when"
        displayName="terms.child_registrations.child_badge_terms"
        name="childBadgeTerms"
        policies={policies}
        refresh={refresh}
      />
      <hr />
      <TermsElement
        allowOrgDelete
        description="terms.displayed_on_the_event_registration"
        displayName="terms.registration_terms"
        name="conventionTerms"
        policies={policies}
        refresh={refresh}
      />
      <hr />
      <TermsElement
        allowOrgDelete
        description="terms.text_to_be_displayed_on"
        displayName="terms.login_page_announcement"
        name="loginAnnouncement"
        policies={policies}
        refresh={refresh}
      />
      <hr />
      <TermsElement
        description="terms.hotel_terms_description"
        displayName="terms.hotel_terms"
        name="hotelReservationTerms"
        policies={policies}
        refresh={refresh}
      />
      <hr />
      <TermsElement
        description="terms.hotel_res_announcement_description"
        displayName="terms.hotel_res_announcement"
        name="hotelReservationAnnouncement"
        policies={policies}
        refresh={refresh}
      />
      <hr />
      <TermsElement
        description="terms.hotel_closed_error_description"
        displayName="terms.hotel_closed_error"
        name="hotelClosedError"
        policies={policies}
        refresh={refresh}
      />
      <hr />
      <TermsElement
        description="terms.child_registration_description"
        displayName="terms.child_registration"
        name="childRegistrationTerms"
        policies={policies}
        refresh={refresh}
      />
      {identityVerification !== 'none' && (
        <>
          <hr />
          <TermsElement
            description="terms.warning_message_to_display_when"
            displayName="terms.opt_out_from_stripe_identity"
            name="optOutIdentity"
            policies={policies}
            refresh={refresh}
          />
        </>
      )}
    </div>
  );
};

interface TermsElementProps {
  readonly allowOrgDelete?: boolean;
  readonly name: string;
  readonly displayName: TranslationKey;
  readonly description: TranslationKey;
  readonly policies: Policy[];
  readonly refresh: () => void;
}

export const TermsElement: FC<TermsElementProps> = ({
  allowOrgDelete,
  description,
  displayName,
  name,
  policies,
  refresh,
}) => {
  const { ts } = useTranslation();
  const [editAnyway, setEditAnyway] = useState(false);
  const matchingPolicies = policies.filter((p) => p.key === name);
  const hasConSpecificPolicy = matchingPolicies.some((p) => !!p.conventionId);

  const createPolicy = useCallback(
    async (orgWide: boolean) => {
      await api.upsertPolicy({
        key: name,
        value: 'TBC',
        orgWide,
      });

      refresh();
    },
    [name, refresh],
  );

  return (
    <div className="start-align mt-10 mb-2">
      <Label for={name}>{ts(displayName)}</Label>
      <FormText color="muted">{ts(description)}</FormText>
      <Row>
        {matchingPolicies.map((policy) => {
          const orgWide = !policy.conventionId;
          const key = orgWide ? `${name}-${policy.conventionId}` : name;

          const allowEditingPolicy = editAnyway || !orgWide || !hasConSpecificPolicy;

          return (
            <Col key={key} md={6} xs={12}>
              <h6>
                {ts(
                  orgWide ? 'terms.organization_wide_policy' : 'terms.convention_specific_policy',
                )}
              </h6>
              {allowEditingPolicy && (
                <TermsTextBox
                  allowOrgDelete={allowOrgDelete}
                  displayName={displayName}
                  name={name}
                  orgWide={orgWide}
                  policy={policy}
                  refresh={refresh}
                />
              )}
              {!allowEditingPolicy && (
                <Alert color="warning">
                  <p>{ts('terms.this_policy_is_overridden_by')}</p>
                  <p>{ts('terms.changes_made_to_the_organizationwide')}</p>
                  <Button
                    className="mt-2"
                    color="danger"
                    onClick={() => {
                      setEditAnyway(true);
                    }}
                    outline
                    size="sm"
                  >
                    {ts('terms.i_understand_but_i_want')}
                  </Button>
                </Alert>
              )}
            </Col>
          );
        })}
        {matchingPolicies.length < 2 && (
          <Col md={6} xs={12}>
            <h6>{ts('terms.create_policies')}</h6>
            {matchingPolicies.length === 0 && (
              <Button
                block
                className="mb-2"
                color="secondary"
                onClick={async () => {
                  await createPolicy(true);
                }}
              >
                {ts('terms.create_organizationwide_policy')}
              </Button>
            )}
            <Button
              block
              className="mb-2"
              color="secondary"
              onClick={async () => {
                await createPolicy(false);
              }}
            >
              {ts('terms.create_conventionspecific_policy')}
            </Button>
          </Col>
        )}
      </Row>
    </div>
  );
};

interface TermsTextBoxProps {
  readonly displayName: TranslationKey;
  readonly name: string;
  readonly policy: Policy;
  readonly orgWide?: boolean;
  readonly allowOrgDelete?: boolean;
  readonly refresh: () => void;
}

const TermsTextBox: FC<TermsTextBoxProps> = ({
  allowOrgDelete,
  displayName,
  policy,
  name,
  orgWide,
  refresh,
}) => {
  const { ts } = useTranslation();
  const [value, setValue] = useInputState(policy.value);
  const [saving, setSaving] = useState(false);

  const onSave = useCallback(async () => {
    try {
      setSaving(true);

      await api.upsertPolicy({
        key: name,
        value,
        orgWide: orgWide ?? false,
      });

      toast.success(
        ts(orgWide ? 'org_wide_policy_saved' : 'convention_specific_policy_saved', ts(displayName)),
      );

      refresh();
    } catch (error) {
      captureError(error as Error);
    }

    setSaving(false);
  }, [name, value, orgWide, ts, displayName, refresh]);

  const onDelete = useCallback(async () => {
    try {
      setSaving(true);

      await api.deletePolicy({
        key: policy.key,
        orgWide: orgWide ?? false,
      });

      toast.success(
        `${
          orgWide ? 'Organization-wide' : 'Convention-specific'
        } policy "${ts(displayName)}" deleted successfully!`,
      );

      refresh();
    } catch (error) {
      captureError(error as Error);
    }

    setSaving(false);
  }, [displayName, orgWide, policy.key, refresh, ts]);

  return (
    <div>
      <Input
        disabled={saving}
        id={name}
        onChange={setValue}
        rows={15}
        type="textarea"
        value={value}
      />
      <Row>
        <Col md={orgWide && !allowOrgDelete ? 12 : 6} xs={12}>
          <Button block className="mt-2" color="primary" disabled={saving} onClick={onSave}>
            {ts('common.save')}
          </Button>
        </Col>
        {(allowOrgDelete || !orgWide) && (
          <Col md={6} xs={12}>
            <Button
              block
              className="mt-2"
              color="danger"
              disabled={saving}
              onClick={onDelete}
              outline
            >
              {ts('common.delete')}
            </Button>
          </Col>
        )}
      </Row>
    </div>
  );
};
